import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { QuillEditorComponent } from 'ngx-quill';
import { MarkdownConverterService } from '@app/core/services/markdown-converter/markdown-converter.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface QuillToolbarConfig {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
  header?: boolean | number[];
  list?: boolean;
  align?: boolean;
  color?: boolean;
  background?: boolean;
  clean?: boolean;
}

export interface EditorConfig {
  theme: 'snow' | 'bubble';
  toolbar: unknown;
  placeholder: string;
  readOnly: boolean;
  formats: string[];
  modules: {
    toolbar: unknown;
    keyboard: unknown;
  };
}

@Component({
  selector: 'tc-rich-text-editor',
  templateUrl: './rich-text-editor.component.html',
  styleUrls: ['./rich-text-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: RichTextEditorComponent,
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RichTextEditorComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @ViewChild('quillEditor', { static: false }) quillEditor!: QuillEditorComponent;

  @Input() placeholder = 'Enter your text here...';
  @Input() readonly = false;
  @Input() theme: 'snow' | 'bubble' = 'snow';
  @Input() toolbarOptions: QuillToolbarConfig = {
    bold: true,
    italic: true,
    underline: true,
    header: true,
    list: true,
    align: true,
    clean: true,
  };
  @Input() showSaveButton = true;
  @Input() minHeight = '60px';
  @Input() set value(markdown: string) {
    if (markdown !== this.currentMarkdown) {
      this.loadMarkdown(markdown || '');
    }
  }

  @Output() readonly contentChange = new EventEmitter<string>();
  @Output() readonly save = new EventEmitter<string>();
  @Output() readonly editorBlur = new EventEmitter<void>();
  @Output() readonly editorFocus = new EventEmitter<void>();

  // Internal state
  private destroy$ = new Subject<void>();
  private contentChange$ = new Subject<string>();
  private currentMarkdown = '';
  private currentHtml = '';
  private isInitialized = false;
  private isTouched = false;

  // ControlValueAccessor
  private onChange = (_value: string) => {
    // This will be overridden by registerOnChange
  };
  private onTouched = () => {
    // This will be overridden by registerOnTouched
  };

  // Quill configuration
  quillConfig!: EditorConfig;

  constructor(private markdownConverter: MarkdownConverterService) {
    this.setupContentChangeDebouncing();
    this.initializeQuillConfig();
  }

  /**
   * Get dynamic editor styles
   */
  get editorStyles() {
    return {
      minHeight: this.minHeight,
    };
  }

  ngOnInit(): void {
    this.initializeQuillConfig();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    if (value !== this.currentMarkdown) {
      this.loadMarkdown(value || '');
      // Reset touched state when value is programmatically set
      this.isTouched = false;
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.readonly = isDisabled;
    this.updateQuillConfig();
  }

  /**
   * Load markdown content into the editor
   */
  loadMarkdown(markdown: string): void {
    try {
      this.currentMarkdown = markdown;

      if (markdown) {
        // Convert markdown to HTML for display in editor
        this.currentHtml = this.markdownConverter.markdownToHtml(markdown);
      } else {
        this.currentHtml = '';
      }

      // Update editor content if initialized
      if (this.quillEditor && this.isInitialized) {
        this.quillEditor.writeValue(this.currentHtml);
      }
    } catch (error) {
      console.error('Error loading markdown:', error);
      this.handleError('Failed to load content');
    }
  }

  /**
   * Handle content changes from Quill editor
   */
  onContentChange(content: string): void {
    if (!this.isInitialized) return;

    this.currentHtml = content;
    this.contentChange$.next(content);
  }

  /**
   * Handle editor ready event
   */
  onEditorCreated(quill: unknown): void {
    this.isInitialized = true;

    // Load initial content if available
    if (this.currentHtml) {
      const quillEditor = quill as { root: { innerHTML: string } };
      quillEditor.root.innerHTML = this.currentHtml;
    }

    // Set up keyboard shortcuts
    this.setupKeyboardShortcuts(quill);
  }

  /**
   * Handle save action (Ctrl+S)
   */
  onSave(): void {
    try {
      // Convert current HTML to markdown
      const markdown = this.markdownConverter.htmlToMarkdown(this.currentHtml);
      this.currentMarkdown = markdown;

      // Emit save event and update form control
      this.save.emit(markdown);
      this.onChange(markdown);
    } catch (error) {
      console.error('Error saving content:', error);
      this.handleError('Failed to save content');
    }
  }

  /**
   * Handle editor focus
   */
  onFocus(): void {
    this.editorFocus.emit();
  }

  /**
   * Handle editor blur
   */
  onBlur(): void {
    // Ensure field is marked as touched even if no content was changed
    if (!this.isTouched) {
      this.isTouched = true;
      this.onTouched();
    }
    this.editorBlur.emit();
  }

  /**
   * Initialize Quill configuration
   */
  private initializeQuillConfig(): void {
    this.quillConfig = {
      theme: this.theme,
      placeholder: this.placeholder,
      readOnly: this.readonly,
      formats: this.getAllowedFormats(),
      toolbar: this.buildToolbarConfig(),
      modules: {
        toolbar: this.buildToolbarConfig(),
        keyboard: {
          bindings: {
            // Custom keyboard bindings will be added in setupKeyboardShortcuts
          },
        },
      },
    };
  }

  /**
   * Update Quill configuration
   */
  private updateQuillConfig(): void {
    this.initializeQuillConfig();
  }

  /**
   * Build toolbar configuration based on options
   */
  private buildToolbarConfig(): unknown[] {
    const toolbar: unknown[] = [];

    this.addFormattingOptions(toolbar);
    this.addHeaderOptions(toolbar);
    this.addListOptions(toolbar);
    this.addAlignOptions(toolbar);
    this.addColorOptions(toolbar);
    this.addCleanOption(toolbar);

    return toolbar;
  }

  private addFormattingOptions(toolbar: unknown[]): void {
    if (this.toolbarOptions.bold || this.toolbarOptions.italic || this.toolbarOptions.underline) {
      const formatting = [];
      if (this.toolbarOptions.bold) formatting.push('bold');
      if (this.toolbarOptions.italic) formatting.push('italic');
      if (this.toolbarOptions.underline) formatting.push('underline');
      if (this.toolbarOptions.strike) formatting.push('strike');
      toolbar.push(formatting);
    }
  }

  private addHeaderOptions(toolbar: unknown[]): void {
    if (this.toolbarOptions.header) {
      if (typeof this.toolbarOptions.header === 'boolean') {
        toolbar.push([{ header: [1, 2, 3, false] }]);
      } else {
        toolbar.push([{ header: this.toolbarOptions.header }]);
      }
    }
  }

  private addListOptions(toolbar: unknown[]): void {
    if (this.toolbarOptions.list) {
      toolbar.push([{ list: 'ordered' }, { list: 'bullet' }]);
    }
  }

  private addAlignOptions(toolbar: unknown[]): void {
    if (this.toolbarOptions.align) {
      toolbar.push([{ align: [] }]);
    }
  }

  private addColorOptions(toolbar: unknown[]): void {
    if (this.toolbarOptions.color || this.toolbarOptions.background) {
      const colors = [];
      if (this.toolbarOptions.color) colors.push({ color: [] });
      if (this.toolbarOptions.background) colors.push({ background: [] });
      toolbar.push(colors);
    }
  }

  private addCleanOption(toolbar: unknown[]): void {
    if (this.toolbarOptions.clean) {
      toolbar.push(['clean']);
    }
  }

  /**
   * Get allowed formats based on toolbar configuration
   */
  private getAllowedFormats(): string[] {
    const formats = [];

    if (this.toolbarOptions.bold) formats.push('bold');
    if (this.toolbarOptions.italic) formats.push('italic');
    if (this.toolbarOptions.underline) formats.push('underline');
    if (this.toolbarOptions.strike) formats.push('strike');
    if (this.toolbarOptions.header) formats.push('header');
    if (this.toolbarOptions.list) formats.push('list');
    if (this.toolbarOptions.align) formats.push('align');
    if (this.toolbarOptions.color) formats.push('color');
    if (this.toolbarOptions.background) formats.push('background');

    return formats;
  }

  /**
   * Setup keyboard shortcuts
   */
  private setupKeyboardShortcuts(quill: unknown): void {
    const quillEditor = quill as {
      keyboard: {
        addBinding: (config: { key: string; ctrlKey: boolean }, handler: () => boolean) => void;
      };
      format: (format: string, value: boolean) => void;
      getFormat: () => { bold?: boolean; italic?: boolean };
    };

    // Ctrl+S for save
    quillEditor.keyboard.addBinding(
      {
        key: 'S',
        ctrlKey: true,
      },
      () => {
        this.onSave();

        return false; // Prevent default browser save
      },
    );

    // Ctrl+B for bold
    if (this.toolbarOptions.bold) {
      quillEditor.keyboard.addBinding(
        {
          key: 'B',
          ctrlKey: true,
        },
        () => {
          quillEditor.format('bold', !quillEditor.getFormat().bold);

          return false;
        },
      );
    }

    // Ctrl+I for italic
    if (this.toolbarOptions.italic) {
      quillEditor.keyboard.addBinding(
        {
          key: 'I',
          ctrlKey: true,
        },
        () => {
          quillEditor.format('italic', !quillEditor.getFormat().italic);

          return false;
        },
      );
    }
  }

  /**
   * Setup content change debouncing
   */
  private setupContentChangeDebouncing(): void {
    this.contentChange$.pipe(
      takeUntil(this.destroy$),
    ).subscribe(content => {
      try {
        // Convert HTML to markdown and emit change
        const markdown = this.markdownConverter.htmlToMarkdown(content);
        this.currentMarkdown = markdown;

        // Mark as touched on first content change for real-time validation
        if (!this.isTouched) {
          this.isTouched = true;
          this.onTouched();
        }

        // Update form control immediately for real-time validation
        this.onChange(markdown);

        this.contentChange.emit(markdown);
      } catch (error) {
        console.error('Error processing content change:', error);
        this.handleError('Failed to process content changes');
      }
    });
  }

  /**
   * Handle errors gracefully
   */
  private handleError(message: string): void {
    // In a real application, you might want to show a toast notification
    // or emit an error event for the parent component to handle
    console.error(`${this.constructor.name}: ${message}`);
  }
}
